import WithGuard from 'HOCs/withGuard'
import { Guard } from 'mobx/Infra/Infra.type'
import { AddressLabel } from 'mobx/Localization/types'
import { getTranslatedTextByKey } from 'utils/language'
import AddressLabelButton from './AddressLabelButton'
import {
	StyledSaveAddressCtaContainer,
	StyledSaveAddressCustomLabelContainer,
	StyledSaveAddressCustomLabelInput,
	StyledSaveAddressCustomLabelTitle,
	StyledSaveAddressHeartContainer,
	StyledSaveAddressHeartIcon,
	StyledSaveAddressHeartOutlineIcon,
	StyledSaveAddressLabelsContainer,
	StyledSaveAddressSectionContainer,
	StyledSaveAddressText,
} from './AddressSavingSection.styles'

interface AddressSavingSectionProps {
	savingAddress: boolean
	label?: AddressLabel
	customLabel?: string
	onChangeLabel: (label: AddressLabel) => void
	onChangeCustomLabel: (label: string) => void
	onToggleSavingAddress: () => void
	disabled?: boolean
}

const getContainerHeight = (savingAddress: boolean, label?: AddressLabel) => {
	if (!savingAddress) {
		return '2rem'
	}

	if (label !== AddressLabel.Other) {
		return '8.625rem'
	}

	return '13.375rem'
}

const AddressSavingSection = ({
	savingAddress,
	label,
	customLabel,
	onChangeLabel,
	onChangeCustomLabel,
	onToggleSavingAddress,
	disabled,
}: AddressSavingSectionProps) => {
	const signupTitle = getTranslatedTextByKey('webviewFlow.localization.signupToSaveAddress', 'Sign up to access and save your addresses')
	const height = getContainerHeight(savingAddress, label)

	return (
		<StyledSaveAddressSectionContainer height={height}>
			<WithGuard
				context={Guard.Context.always}
				type={Guard.Type.event}
				name={Guard.Name.saveAddress}
				overrideSignupParams={{ signupTitle, signupDisplaySkipSection: false }}
			>
				<StyledSaveAddressCtaContainer data-testid="save-address-container" onClick={onToggleSavingAddress} disabled={disabled}>
					<StyledSaveAddressHeartContainer>
						<StyledSaveAddressHeartOutlineIcon $show={!savingAddress} />
						<StyledSaveAddressHeartIcon data-testid="save-address-heart-icon" $show={savingAddress} />
					</StyledSaveAddressHeartContainer>
					<StyledSaveAddressText data-testid="save-address-text" variant="BodyRegular">
						{getTranslatedTextByKey('webviewFlow.localization.saveNewAddress', 'Add to my saved addresses')}
					</StyledSaveAddressText>
				</StyledSaveAddressCtaContainer>
			</WithGuard>
			<StyledSaveAddressLabelsContainer show={savingAddress}>
				<AddressLabelButton
					onClick={() => onChangeLabel(AddressLabel.Home)}
					selected={label === AddressLabel.Home}
					iconUrl="/icons/homeLabel.svg"
					text={getTranslatedTextByKey('webviewFlow.localization.homeLabel', 'Home')}
				/>
				<AddressLabelButton
					onClick={() => onChangeLabel(AddressLabel.Work)}
					selected={label === AddressLabel.Work}
					iconUrl="/icons/workLabel.svg"
					text={getTranslatedTextByKey('webviewFlow.localization.workLabel', 'Work')}
				/>
				<AddressLabelButton
					onClick={() => onChangeLabel(AddressLabel.Other)}
					selected={label === AddressLabel.Other}
					iconUrl="/icons/otherLabel.svg"
					text={getTranslatedTextByKey('webviewFlow.localization.otherLabel', 'Other')}
				/>
			</StyledSaveAddressLabelsContainer>
			<StyledSaveAddressCustomLabelContainer show={savingAddress && label === AddressLabel.Other}>
				<StyledSaveAddressCustomLabelTitle variant="BodySmallHighlighted">
					{getTranslatedTextByKey('webviewFlow.localization.customLabel', 'Address label')}
				</StyledSaveAddressCustomLabelTitle>
				<StyledSaveAddressCustomLabelInput
					value={customLabel}
					onChange={(e) => onChangeCustomLabel(e.target.value)}
					onResetValue={() => {
						onChangeCustomLabel('')
					}}
					inputProps={{ 'data-testid': 'map-dialog-input' }}
					placeholder={getTranslatedTextByKey('webviewFlow.localization.customLabelPlaceholder', 'e.g. home')}
					selected={!!customLabel}
				/>
			</StyledSaveAddressCustomLabelContainer>
		</StyledSaveAddressSectionContainer>
	)
}

export default AddressSavingSection
